'use client'

import type { Didomi } from '@/util/analytics'

import type { FreestarSuppressionReason } from './types'

export type FreestarConsentResult =
    | { status: 'allowed'; didomi: Didomi }
    | {
          status: 'suppressed'
          reason: Extract<FreestarSuppressionReason, 'consent_suppressed' | 'didomi_unavailable'>
      }

const freestarConsentPromises = new Map<string, Promise<FreestarConsentResult>>()
let freestarDidomiPromise: Promise<Didomi> | undefined

export const FREESTAR_DIDOMI_VENDOR_API_ID = 'publisher-hnyDWaPx'
export const FREESTAR_IAB_VENDOR_ID = '850'

function suppress(reason: Extract<FreestarSuppressionReason, 'consent_suppressed' | 'didomi_unavailable'>) {
    return { status: 'suppressed' as const, reason }
}

export function resolveFreestarConsentFromDidomi(
    didomi: Didomi | undefined,
    vendorId: string = FREESTAR_DIDOMI_VENDOR_API_ID
): Promise<FreestarConsentResult> {
    if (!didomi) {
        return Promise.resolve(suppress('didomi_unavailable'))
    }

    let userStatus: ReturnType<NonNullable<Didomi['getCurrentUserStatus']>> | undefined
    try {
        userStatus = didomi.getCurrentUserStatus?.()
    } catch {
        return Promise.resolve(suppress('didomi_unavailable'))
    }

    const vendorStatus = userStatus?.vendors?.[vendorId]
    if (!vendorStatus) {
        return Promise.resolve(suppress('didomi_unavailable'))
    }
    if (vendorStatus.enabled === true) {
        return Promise.resolve({ status: 'allowed', didomi })
    }
    if (!didomi.isUserStatusPartial) {
        return Promise.resolve(suppress('didomi_unavailable'))
    }
    if (!didomi.isUserStatusPartial()) {
        return Promise.resolve(suppress('consent_suppressed'))
    }
    if (!didomi.addVendorStatusListener) {
        return Promise.resolve(suppress('didomi_unavailable'))
    }

    return new Promise((resolve) => {
        let settled = false
        didomi.addVendorStatusListener?.(vendorId, (nextVendorStatus) => {
            if (settled) {
                return
            }
            settled = true
            resolve(nextVendorStatus.enabled ? { status: 'allowed', didomi } : suppress('consent_suppressed'))
        })
    })
}

export function waitForFreestarConsent(
    vendorId: string = FREESTAR_DIDOMI_VENDOR_API_ID
): Promise<FreestarConsentResult> {
    if (typeof window === 'undefined') {
        return Promise.resolve(suppress('didomi_unavailable'))
    }

    const cachedConsent = freestarConsentPromises.get(vendorId)
    if (cachedConsent) {
        return cachedConsent
    }

    const consentPromise = waitForFreestarConsentResult(vendorId)
    freestarConsentPromises.set(vendorId, consentPromise)
    return consentPromise
}

export function waitForFreestarDidomi(): Promise<Didomi | undefined> {
    if (typeof window === 'undefined') {
        return Promise.resolve(undefined)
    }

    if (freestarDidomiPromise) {
        return freestarDidomiPromise
    }

    freestarDidomiPromise = waitForFreestarDidomiResult()
    return freestarDidomiPromise
}

function waitForFreestarConsentResult(vendorId: string): Promise<FreestarConsentResult> {
    if (window.Didomi) {
        waitForFreestarDidomi()
        return resolveFreestarConsentFromDidomi(window.Didomi, vendorId)
    }

    return waitForFreestarDidomi().then((didomi) => resolveFreestarConsentFromDidomi(didomi, vendorId))
}

function waitForFreestarDidomiResult(): Promise<Didomi> {
    if (window.Didomi) {
        return Promise.resolve(window.Didomi)
    }

    window.didomiOnReady = window.didomiOnReady || []
    return new Promise((resolve) => {
        window.didomiOnReady.push((didomi) => {
            resolve(didomi)
        })
    })
}
